import { useEffect, useRef, useState } from 'react';
import { Brain } from '@phosphor-icons/react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/components/ui/accordion';
import { cn } from '~/lib/utils';

interface ReasoningBlockProps {
  text: string;
  /** True while the message is still streaming — drives auto-expand + pulse. */
  isStreaming?: boolean;
}

/**
 * Collapsible "thinking" surface for reasoning models (Claude extended
 * thinking, OpenAI o-series, Gemini deep-think variants).
 *
 * UX rules:
 *   - While streaming, the block auto-expands so the user can watch the
 *     model reason. The header pulses to signal "live".
 *   - When the stream finishes, the block collapses back to a one-line
 *     summary the user can click to re-open. Counts characters so the
 *     header reads "Thought for 1,283 chars" — a cheap stand-in for
 *     tokens without a tokenizer roundtrip.
 *   - User toggles win: an explicit click locks the open/closed state
 *     and the auto-collapse no longer fires for this message.
 */
export function ReasoningBlock({ text, isStreaming }: ReasoningBlockProps) {
  const [userToggled, setUserToggled] = useState(false);
  // A finished message (history, reload) mounts closed — opening it and
  // collapsing a moment later shifts every message below it.
  const [open, setOpen] = useState(() => Boolean(isStreaming));
  const startedAt = useRef<number>(Date.now());
  const finishedAt = useRef<number | null>(null);
  // Only a stream this block actually watched has a meaningful duration;
  // timing a mount-to-effect gap would read "Thought for 7ms".
  const sawStreaming = useRef(Boolean(isStreaming));

  // Track when streaming flipped from true → false so we can show a
  // "thought for Xs" duration. The ref-based capture survives re-renders
  // and doesn't reset when the parent message updates.
  useEffect(() => {
    if (isStreaming) {
      sawStreaming.current = true;
      finishedAt.current = null;
    } else if (sawStreaming.current && finishedAt.current == null) {
      finishedAt.current = Date.now();
    }
  }, [isStreaming]);

  // Auto-collapse once streaming finishes — unless the user has clicked
  // to lock the state. We give a short grace window so the last reasoning
  // tokens stay visible before the block snaps shut.
  useEffect(() => {
    if (isStreaming || userToggled || !open) return;
    const t = setTimeout(() => setOpen(false), 600);
    return () => clearTimeout(t);
  }, [isStreaming, userToggled, open]);

  if (!text) return null;

  const chars = text.length;
  const durationMs =
    finishedAt.current != null ? finishedAt.current - startedAt.current : null;
  const durationLabel =
    durationMs != null
      ? durationMs < 1000
        ? `${durationMs}ms`
        : `${(durationMs / 1000).toFixed(1)}s`
      : null;

  const headerLabel = isStreaming
    ? 'Thinking…'
    : durationLabel
      ? `Thought for ${durationLabel} · ${chars.toLocaleString()} chars`
      : `Thinking · ${chars.toLocaleString()} chars`;

  return (
    // Controlled Accordion: `open` is driven by the streaming auto-expand /
    // auto-collapse logic above. onValueChange only fires on real user
    // clicks, so an explicit toggle still locks the state (userToggled).
    <Accordion
      type="single"
      collapsible
      value={open ? 'reasoning' : ''}
      onValueChange={(v) => {
        setUserToggled(true);
        setOpen(v === 'reasoning');
      }}
      className="my-1.5 max-w-[85%]"
    >
      <AccordionItem value="reasoning" className="border-b-0">
        <AccordionTrigger
          className={cn(
            'w-fit flex-none items-center gap-1.5 py-0 text-[length:var(--text-2xs)] font-medium tracking-tight transition-colors',
            'text-muted-foreground hover:text-foreground hover:no-underline',
            '[&>svg]:size-2.5 [&>svg]:text-muted-foreground/70',
            isStreaming && 'text-foreground/80',
          )}
        >
          <span
            className={cn(
              'inline-flex items-center justify-center w-4 h-4 rounded-full',
              'bg-muted text-muted-foreground',
              isStreaming && 'bg-primary/15 text-primary animate-pulse',
            )}
          >
            <Brain size={10} weight={isStreaming ? 'fill' : 'regular'} />
          </span>
          <span>{headerLabel}</span>
        </AccordionTrigger>
        {/*
          Border on the left makes it read as a quote / aside; italic
          muted type keeps it distinct from the visible reply below.
        */}
        <AccordionContent className="pb-0">
          <div
            className={cn(
              'mt-1 pl-2.5 ml-1 border-l-2 border-primary/30',
              'text-xs leading-relaxed text-muted-foreground italic',
              'whitespace-pre-wrap break-words',
            )}
          >
            {text}
            {isStreaming && (
              <span className="inline-block w-1 h-3 ml-0.5 align-text-bottom bg-primary/60 animate-pulse" />
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
